// Simple localStorage-backed attendance store and helpers

const STORAGE_KEY = "attendance.records.v1";
const HOLIDAYS_KEY = "attendance.holidays.v1"; // optional array of ISO dates
const LEAVES_KEY = "attendance.leaves.v1"; // optional array of {date, type: 'full'|'half-am'|'half-pm'}

export function getAllRecords() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export function saveAllRecords(records) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(records));
}

export function getTodayKey(date = new Date()) {
  return date.toISOString().slice(0, 10); // YYYY-MM-DD
}

export function isHoliday(date = new Date()) {
  try {
    const raw = localStorage.getItem(HOLIDAYS_KEY);
    const days = raw ? JSON.parse(raw) : [];
    return days.includes(getTodayKey(date));
  } catch {
    return false;
  }
}

export function getLeaveForDate(date = new Date()) {
  try {
    const raw = localStorage.getItem(LEAVES_KEY);
    const leaves = raw ? JSON.parse(raw) : [];
    return leaves.find((l) => l.date === getTodayKey(date)) || null;
  } catch {
    return null;
  }
}

export function getTodayRecord(date = new Date()) {
  const key = getTodayKey(date);
  return getAllRecords().find((r) => r.date === key) || null;
}

export function canClockIn(date = new Date()) {
  if (isHoliday(date)) return { allowed: false, reason: "holiday" };
  const leave = getLeaveForDate(date);
  if (leave && leave.type === "full")
    return { allowed: false, reason: "leave" };
  // Half-day leave still allows exactly one of clock-in/out depending on half
  return { allowed: true, reason: leave ? leave.type : null };
}

export function clockIn({
  imageDataUrl,
  locationText,
  locationCoords,
  date = new Date(),
}) {
  const key = getTodayKey(date);
  const records = getAllRecords();
  const existing = records.find((r) => r.date === key);
  if (existing && existing.clockIn) return existing; // already clocked in
  const leave = getLeaveForDate(date);
  const record = existing || {
    id: `${key}-${Date.now()}`,
    date: key,
    location: locationText,
    type: leave ? (leave.type === "full" ? "leave" : "half") : "present",
  };
  if (
    locationCoords &&
    typeof locationCoords.lat === "number" &&
    typeof locationCoords.lng === "number"
  ) {
    record.locationCoords = {
      lat: Number(locationCoords.lat),
      lng: Number(locationCoords.lng),
    };
  }
  if (locationText) {
    record.location = locationText;
  }
  record.clockIn = new Date().toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
  record.photo = imageDataUrl;
  const next = existing
    ? records.map((r) => (r.date === key ? record : r))
    : [record, ...records];
  saveAllRecords(next);
  return record;
}

export function clockOut({
  date = new Date(),
  imageDataUrl,
  locationText,
  locationCoords,
} = {}) {
  const key = getTodayKey(date);
  const records = getAllRecords();
  const existing = records.find((r) => r.date === key);
  if (!existing) return null;
  if (existing.clockOut) return existing;
  existing.clockOut = new Date().toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
  if (imageDataUrl) existing.clockOutPhoto = imageDataUrl;
  if (locationText) existing.clockOutLocation = locationText;
  if (
    locationCoords &&
    typeof locationCoords.lat === "number" &&
    typeof locationCoords.lng === "number"
  ) {
    existing.clockOutCoords = {
      lat: Number(locationCoords.lat),
      lng: Number(locationCoords.lng),
    };
  }
  saveAllRecords(records);
  return existing;
}

export function updateTodayRecord({
  date = new Date(),
  locationText,
  locationCoords,
  clockOutLocation,
  clockOutCoords,
} = {}) {
  const key = getTodayKey(date);
  const records = getAllRecords();
  const existing = records.find((r) => r.date === key);
  if (!existing) return null;

  let changed = false;

  // Only update clock-in location if clock-in hasn't happened yet
  // This prevents clock-out location updates from overwriting clock-in data
  if (locationText && !existing.clockIn && existing.location !== locationText) {
    existing.location = locationText;
    changed = true;
  }

  // Only update clock-in coordinates if clock-in hasn't happened yet
  if (
    locationCoords &&
    !existing.clockIn &&
    typeof locationCoords.lat === "number" &&
    typeof locationCoords.lng === "number"
  ) {
    const lat = Number(locationCoords.lat);
    const lng = Number(locationCoords.lng);
    if (
      !existing.locationCoords ||
      existing.locationCoords.lat !== lat ||
      existing.locationCoords.lng !== lng
    ) {
      existing.locationCoords = { lat, lng };
      changed = true;
    }
  }

  // Clock-out location updates (separate from clock-in)
  if (clockOutLocation && existing.clockOutLocation !== clockOutLocation) {
    existing.clockOutLocation = clockOutLocation;
    changed = true;
  }

  if (
    clockOutCoords &&
    typeof clockOutCoords.lat === "number" &&
    typeof clockOutCoords.lng === "number"
  ) {
    const lat = Number(clockOutCoords.lat);
    const lng = Number(clockOutCoords.lng);
    if (
      !existing.clockOutCoords ||
      existing.clockOutCoords.lat !== lat ||
      existing.clockOutCoords.lng !== lng
    ) {
      existing.clockOutCoords = { lat, lng };
      changed = true;
    }
  }

  if (changed) {
    saveAllRecords(records);
  }

  return existing;
}

export function isOlderThan90Days(isoDate) {
  const then = new Date(isoDate + "T00:00:00");
  const ninetyDaysMs = 90 * 24 * 60 * 60 * 1000;
  return Date.now() - then.getTime() > ninetyDaysMs;
}
